<script setup>
import { computed, ref, onMounted, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';
import { useDealsStore } from 'dashboard/stores/deals';
import { useElementSize, onClickOutside } from '@vueuse/core';
import BackButton from '../BackButton.vue';
import InboxName from '../InboxName.vue';
import { getContactIdentifier, isEmailChannel } from 'dashboard/helper/inbox';
import MoreActions from './MoreActions.vue';
import Avatar from 'next/avatar/Avatar.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import SLACardLabel from './components/SLACardLabel.vue';
import ConversationCallButton from './ConversationCallButton.vue';
import wootConstants from 'dashboard/constants/globals';
import { conversationListPageURL } from 'dashboard/helper/URLHelper';
import { snoozedReopenTime } from 'dashboard/helper/snoozeHelpers';
import { useInbox } from 'dashboard/composables/useInbox';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import { copyTextToClipboard } from 'shared/helpers/clipboard';

const props = defineProps({
  chat: {
    type: Object,
    default: () => ({}),
  },
  showBackButton: {
    type: Boolean,
    default: false,
  },
});

const { t } = useI18n();
const store = useStore();
const dealsStore = useDealsStore();
const route = useRoute();
const conversationHeader = ref(null);
const { width } = useElementSize(conversationHeader);
const { isAWebWidgetInbox } = useInbox();

const currentChat = computed(() => store.getters.getSelectedChat);
const accountId = computed(() => store.getters.getCurrentAccountId);

const chatMetadata = computed(() => props.chat.meta);

const backButtonUrl = computed(() => {
  const {
    params: { inbox_id: inboxId, label, teamId, id: customViewId },
    name,
  } = route;

  const conversationTypeMap = {
    conversation_through_mentions: 'mention',
    conversation_through_participating: 'participating',
    conversation_through_unattended: 'unattended',
  };
  return conversationListPageURL({
    accountId: accountId.value,
    inboxId,
    label,
    teamId,
    conversationType: conversationTypeMap[name],
    customViewId,
  });
});

const isHMACVerified = computed(() => {
  if (!isAWebWidgetInbox.value) {
    return true;
  }
  return chatMetadata.value.hmac_verified;
});

const currentContact = computed(() =>
  store.getters['contacts/getContact'](props.chat.meta.sender.id)
);

const isSnoozed = computed(
  () => currentChat.value.status === wootConstants.STATUS_TYPE.SNOOZED
);

const snoozedDisplayText = computed(() => {
  const { snoozed_until: snoozedUntil } = currentChat.value;
  if (snoozedUntil) {
    return `${t('CONVERSATION.HEADER.SNOOZED_UNTIL')} ${snoozedReopenTime(snoozedUntil)}`;
  }
  return t('CONVERSATION.HEADER.SNOOZED_UNTIL_NEXT_REPLY');
});

const inbox = computed(() => {
  const { inbox_id: inboxId } = props.chat;
  return store.getters['inboxes/getInbox'](inboxId);
});

const hasMultipleInboxes = computed(
  () => store.getters['inboxes/getInboxes'].length > 1
);

// Identificativo del contatto (mail intera / numero / @username) accanto ai dati
// conversazione, cosi' e' visibile anche dentro la chat, non solo nella lista.
const contactIdentifier = computed(() =>
  getContactIdentifier(currentContact.value, inbox.value?.channel_type)
);

const emailSubject = computed(() => {
  if (!isEmailChannel(inbox.value)) return '';
  const attrs =
    props.chat.additional_attributes || props.chat.additionalAttributes || {};
  return String(attrs.mail_subject || attrs.mailSubject || '').trim();
});

const headerTitle = computed(
  () => emailSubject.value || currentContact.value?.name
);

const emailSubtitle = computed(() =>
  [currentContact.value?.name, contactIdentifier.value]
    .filter(Boolean)
    .join(' · ')
);

// Deal collegato: pill con la fase (pallino colorato + nome), tooltip col nome del deal.
const dealBadge = computed(() => {
  const deals = props.chat.deals;
  if (!deals || !deals.length) return null;
  const [first] = deals;
  const stage = first.stage || {};
  const extra = deals.length > 1 ? ` +${deals.length - 1}` : '';
  return {
    color: stage.color || '#64748B',
    label: `${stage.name || first.name}${extra}`,
    title: deals.map(deal => deal.name).join(', '),
  };
});

// Deal + fasi disponibili per il cambio rapido di fase dalla label in header.
const firstDeal = computed(() => props.chat.deals?.[0] || null);

const dealStages = computed(() => {
  const deal = firstDeal.value;
  if (!deal) return [];
  const pipeline = (dealsStore.pipelines || []).find(
    p => p.id === deal.pipelineId || p.id === deal.pipeline_id
  );
  return pipeline?.stages || [];
});

const dealStageOptions = computed(() =>
  dealStages.value.map(stage => ({ value: stage.id, label: stage.name }))
);

const currentStageId = computed(
  () => firstDeal.value?.pipelineStageId || firstDeal.value?.pipeline_stage_id
);

// "Le mie società" che seguono il deal (le chiavi arrivano snake_case dal serializer lista).
const ownerCompanies = computed(() => {
  const deal = firstDeal.value;
  const list = deal?.ownerCompanies || deal?.owner_companies || [];
  return Array.isArray(list) ? list : [];
});

// Fase corrente (pallino + nome) mostrata nella pill prominente.
const currentStageName = computed(
  () => firstDeal.value?.stage?.name || dealBadge.value?.label || ''
);
const currentStageColor = computed(
  () => firstDeal.value?.stage?.color || '#64748B'
);

const onDealStageChange = async stageId => {
  const deal = firstDeal.value;
  if (!deal || stageId === currentStageId.value) return;
  try {
    await dealsStore.changeDealStage(deal.id, stageId);
    // Rinfresca la conversazione così la label riflette subito la nuova fase.
    store.dispatch('getConversation', props.chat.id);
  } catch (error) {
    useAlert(error.message || t('CONVERSATION.HEADER.STAGE_CHANGE_ERROR'));
  }
};

// --- "Le mie società": popover a click (niente hover, niente sottomenu che sparisce) ---
const MAX_OWNER_COMPANIES = 2;
const showCompanyPopover = ref(false);
const companyPopoverRef = ref(null);
const companyQuery = ref('');
// Copia locale ottimistica: le spunte cambiano subito, poi si persiste.
const localOwnerCompanies = ref([]);
watch(
  ownerCompanies,
  list => {
    localOwnerCompanies.value = [...list];
  },
  { immediate: true }
);
onClickOutside(companyPopoverRef, () => {
  showCompanyPopover.value = false;
});

const canAddCompany = computed(
  () => localOwnerCompanies.value.length < MAX_OWNER_COMPANIES
);
const companyItems = computed(() => {
  const query = companyQuery.value.trim().toLowerCase();
  const selected = new Set(
    localOwnerCompanies.value.map(name => name.toLowerCase())
  );
  const suggestions = dealsStore.ownerCompanySuggestions || [];
  const merged = [
    ...localOwnerCompanies.value,
    ...suggestions.filter(name => !selected.has(name.toLowerCase())),
  ];
  return merged.filter(name => !query || name.toLowerCase().includes(query));
});
const typedIsNew = computed(() => {
  const value = companyQuery.value.trim().toLowerCase();
  if (!value) return false;
  return !companyItems.value.some(name => name.toLowerCase() === value);
});
const isCompanySelected = name =>
  localOwnerCompanies.value.some(c => c.toLowerCase() === name.toLowerCase());

const persistCompanies = async () => {
  const deal = firstDeal.value;
  if (!deal) return;
  try {
    await dealsStore.updateDeal(deal.id, {
      ownerCompanies: localOwnerCompanies.value,
    });
    store.dispatch('getConversation', props.chat.id);
  } catch (error) {
    useAlert(error.message || t('CONVERSATION.HEADER.STAGE_CHANGE_ERROR'));
  }
};
const toggleCompany = name => {
  const value = name.trim();
  if (!value) return;
  if (isCompanySelected(value)) {
    localOwnerCompanies.value = localOwnerCompanies.value.filter(
      c => c.toLowerCase() !== value.toLowerCase()
    );
  } else {
    if (!canAddCompany.value) return;
    localOwnerCompanies.value = [...localOwnerCompanies.value, value];
  }
  persistCompanies();
};
const addCompanyFromInput = () => {
  const value = companyQuery.value.trim();
  companyQuery.value = '';
  if (!value || !canAddCompany.value || isCompanySelected(value)) return;
  localOwnerCompanies.value = [...localOwnerCompanies.value, value];
  persistCompanies();
};

// Creazione deal dall'header, istantanea (niente popup): crea al volo un deal nella
// pipeline di default (prima fase), intestato al contatto e collegato alla conversazione,
// poi rinfresca così compaiono subito Fase + Società. Rinominabile aprendo il deal.
const isCreatingDeal = ref(false);
const createDealQuick = async () => {
  if (isCreatingDeal.value) return;
  isCreatingDeal.value = true;
  try {
    if (!dealsStore.pipelines?.length) await dealsStore.fetchPipelines();
    const pipeline =
      dealsStore.pipelines.find(p => p.isDefault) || dealsStore.pipelines[0];
    if (!pipeline) {
      useAlert(t('CONVERSATION.HEADER.CREATE_DEAL_NO_PIPELINE'));
      return;
    }
    await dealsStore.createDeal({
      name: currentContact.value?.name || `#${props.chat.id}`,
      pipelineId: pipeline.id,
      contactId: props.chat.meta?.sender?.id,
      conversationId: props.chat.id,
    });
    store.dispatch('getConversation', props.chat.id);
  } catch (error) {
    useAlert(error.message || t('CONVERSATION.HEADER.CREATE_DEAL_ERROR'));
  } finally {
    isCreatingDeal.value = false;
  }
};

onMounted(() => {
  if (!dealsStore.pipelines?.length)
    dealsStore.fetchPipelines?.().catch(() => {});
  // Carica i deal (silenzioso, una volta a sessione) per popolare l'autocomplete
  // "le mie società": qui la board non li ha caricati.
  if (!dealsStore.deals?.length)
    dealsStore.fetchDeals?.({}, { silent: true }).catch(() => {});
});

const hasSlaPolicyId = computed(
  () => props.chat?.applied_sla?.id && !currentContact.value?.blocked
);

const copyConversationId = async () => {
  try {
    await copyTextToClipboard(String(props.chat.id));
    useAlert(t('CONVERSATION.HEADER.COPY_ID_SUCCESS'));
  } catch (error) {
    // error
  }
};
</script>

<template>
  <div
    ref="conversationHeader"
    class="flex flex-col gap-3 items-center justify-between flex-1 w-full min-w-0 xl:flex-row px-3 pt-3 pb-2 h-24 xl:h-12"
  >
    <div
      class="flex items-center justify-start w-full xl:w-auto max-w-full min-w-0 xl:flex-1"
    >
      <BackButton
        v-if="showBackButton"
        :back-url="backButtonUrl"
        class="ltr:mr-2 rtl:ml-2"
      />
      <Avatar
        :name="currentContact.name"
        :src="currentContact.thumbnail"
        :size="32"
        :status="currentContact.availability_status"
        hide-offline-status
      />
      <div
        class="flex flex-col items-start min-w-0 ml-2 overflow-hidden rtl:ml-0 rtl:mr-2"
      >
        <div class="flex flex-row items-baseline max-w-full gap-1.5 p-0 m-0">
          <span
            class="text-sm font-medium truncate leading-tight text-n-slate-12 flex-shrink min-w-0"
            :title="headerTitle"
          >
            {{ headerTitle }}
          </span>
          <span
            v-if="emailSubject"
            :title="emailSubtitle"
            class="text-xs leading-tight truncate text-n-slate-10 flex-shrink-0 max-w-[55%]"
          >
            {{ emailSubtitle }}
          </span>
          <span
            v-else-if="contactIdentifier"
            :title="contactIdentifier"
            class="text-xs leading-tight truncate text-n-slate-10 flex-shrink-0 max-w-[55%]"
          >
            {{ contactIdentifier }}
          </span>
          <fluent-icon
            v-if="!isHMACVerified"
            v-tooltip="$t('CONVERSATION.UNVERIFIED_SESSION')"
            size="14"
            class="text-n-amber-10 my-0 mx-0 min-w-[14px] flex-shrink-0"
            icon="warning"
          />
        </div>

        <div
          class="flex items-center gap-1 overflow-hidden text-xs conversation--header--actions text-n-slate-11 text-ellipsis whitespace-nowrap"
        >
          <button
            type="button"
            class="truncate text-label-small text-n-slate-11 hover:text-n-slate-12 !p-0 cucursor-pointer"
            @click="copyConversationId"
          >
            {{ `#${chat.id}` }}
          </button>
          <span v-if="hasMultipleInboxes">•</span>
          <InboxName
            v-if="hasMultipleInboxes"
            :inbox="inbox"
            :emphasize="isEmailChannel(inbox)"
            class="!mx-0 max-w-[18rem]"
          />
          <!-- Fase e Società sono controlli prominenti nel gruppo azioni a destra
               (fuori da questo overflow-hidden, così il popover non viene tagliato). -->
          <span v-if="isSnoozed">•</span>
          <span v-if="isSnoozed" class="font-medium text-n-amber-10">
            {{ snoozedDisplayText }}
          </span>
        </div>
      </div>
    </div>
    <div
      class="flex flex-row items-center justify-start xl:justify-end flex-shrink-0 gap-2 w-full xl:w-auto header-actions-wrap"
    >
      <!-- Nessun deal collegato: crea al volo (un click, niente popup). -->
      <button
        v-if="!firstDeal"
        type="button"
        :disabled="isCreatingDeal"
        class="inline-flex items-center gap-1.5 h-7 px-2 rounded-md border border-n-weak bg-n-alpha-1 hover:bg-n-alpha-2 flex-shrink-0 disabled:opacity-60"
        @click="createDealQuick"
      >
        <Icon icon="i-lucide-plus" class="size-3.5 text-n-slate-10" />
        <span class="text-xs font-medium text-n-slate-11">
          {{ t('CONVERSATION.HEADER.CREATE_DEAL') }}
        </span>
      </button>
      <!-- FASE: pill prominente. Il select nativo (robusto, niente hover) copre la pill in
           trasparenza: click ovunque = apre il menu nativo di sistema. -->
      <span
        v-if="firstDeal && dealStageOptions.length"
        :title="dealBadge?.title"
        class="relative inline-flex items-center gap-1.5 h-7 rounded-md border border-n-weak bg-n-alpha-1 hover:bg-n-alpha-2 ltr:pl-2 ltr:pr-6 rtl:pr-2 rtl:pl-6 flex-shrink-0"
      >
        <span
          class="rounded-full size-2 flex-shrink-0"
          :style="{ backgroundColor: currentStageColor }"
        />
        <span class="text-xs font-medium truncate max-w-[8rem] text-n-slate-12">
          {{ currentStageName }}
        </span>
        <Icon
          icon="i-lucide-chevron-down"
          class="absolute size-3.5 pointer-events-none text-n-slate-10 ltr:right-1.5 rtl:left-1.5"
        />
        <select
          :value="currentStageId"
          class="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
          @change="onDealStageChange(Number($event.target.value))"
        >
          <option
            v-for="opt in dealStageOptions"
            :key="opt.value"
            :value="opt.value"
          >
            {{ opt.label }}
          </option>
        </select>
      </span>
      <!-- Fase read-only finché le pipeline non sono caricate -->
      <span
        v-else-if="firstDeal"
        class="inline-flex items-center gap-1.5 h-7 px-2 rounded-md border border-n-weak bg-n-alpha-1 flex-shrink-0"
      >
        <span
          class="rounded-full size-2 flex-shrink-0"
          :style="{ backgroundColor: currentStageColor }"
        />
        <span class="text-xs font-medium truncate max-w-[8rem] text-n-slate-12">
          {{ currentStageName }}
        </span>
      </span>

      <!-- SOCIETÀ: popover a click che si apre verso il basso (niente hover/sottomenu). -->
      <div
        v-if="firstDeal"
        ref="companyPopoverRef"
        class="relative inline-flex flex-shrink-0"
      >
        <button
          type="button"
          :title="localOwnerCompanies.join(', ')"
          class="inline-flex items-center gap-1.5 h-7 px-2 rounded-md border border-n-weak bg-n-alpha-1 hover:bg-n-alpha-2 max-w-[11rem]"
          @click.stop="showCompanyPopover = !showCompanyPopover"
        >
          <Icon
            icon="i-lucide-building-2"
            class="size-3.5 text-n-slate-10 flex-shrink-0"
          />
          <span
            v-if="localOwnerCompanies.length"
            class="text-xs font-medium truncate text-n-slate-12"
          >
            {{ localOwnerCompanies.join(', ') }}
          </span>
          <span v-else class="text-xs text-n-slate-11">
            {{ t('CONVERSATION.HEADER.ADD_COMPANY') }}
          </span>
          <Icon
            icon="i-lucide-chevron-down"
            class="size-3.5 text-n-slate-10 flex-shrink-0"
          />
        </button>
        <div
          v-if="showCompanyPopover"
          class="absolute z-30 w-64 p-1 border rounded-lg shadow-xl top-8 border-n-weak bg-n-solid-1 ltr:left-0 rtl:right-0"
        >
          <input
            v-model="companyQuery"
            type="text"
            :placeholder="t('CONVERSATION.HEADER.COMPANY_SEARCH')"
            class="w-full px-2 py-1.5 mb-1 text-xs border rounded-md border-n-weak bg-n-alpha-1 text-n-slate-12 focus:outline-none"
            @keyup.enter="addCompanyFromInput"
            @click.stop
          />
          <div class="overflow-y-auto max-h-48">
            <button
              v-for="name in companyItems"
              :key="name"
              type="button"
              class="flex items-center w-full gap-2 px-2 py-1.5 text-left rounded-md hover:bg-n-alpha-2"
              @click.stop="toggleCompany(name)"
            >
              <span class="text-xs truncate grow text-n-slate-12">{{
                name
              }}</span>
              <Icon
                v-if="isCompanySelected(name)"
                icon="i-lucide-check"
                class="size-3.5 text-n-brand flex-shrink-0"
              />
            </button>
            <button
              v-if="typedIsNew && canAddCompany"
              type="button"
              class="flex items-center w-full gap-2 px-2 py-1.5 text-left rounded-md hover:bg-n-alpha-2 text-n-slate-11"
              @click.stop="addCompanyFromInput"
            >
              <Icon icon="i-lucide-plus" class="size-3.5 flex-shrink-0" />
              <span class="text-xs truncate">{{
                t('CONVERSATION.HEADER.COMPANY_ADD', {
                  name: companyQuery.trim(),
                })
              }}</span>
            </button>
            <p
              v-if="!canAddCompany"
              class="px-2 py-1.5 text-xs text-n-slate-10"
            >
              {{ t('CONVERSATION.HEADER.COMPANY_MAX') }}
            </p>
            <p
              v-else-if="!companyItems.length && !typedIsNew"
              class="px-2 py-1.5 text-xs text-n-slate-10"
            >
              {{ t('CONVERSATION.HEADER.COMPANY_EMPTY') }}
            </p>
          </div>
        </div>
      </div>

      <SLACardLabel
        v-if="hasSlaPolicyId"
        :chat="chat"
        show-extended-info
        :parent-width="width"
        class="hidden md:flex"
      />
      <ConversationCallButton :inbox="inbox" :chat="currentChat" />
      <MoreActions :conversation-id="currentChat.id" />
    </div>
  </div>
</template>
